import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Lock, Pencil, Package, Loader, CheckCircle2, FileText, Plus, HelpCircle,
  UserRound, ClipboardList, ArrowRight, FolderOpen, CreditCard, MessageSquare,
  RefreshCw, Info, AlertTriangle, CalendarDays, Download, Truck, History,
  Check, Languages, Banknote, Eye, Inbox, Flame, RotateCcw, SlidersHorizontal,
  Hourglass, ChevronLeft, Receipt, Printer, Star, X,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { clientApi, fileUrl } from '../../api/client';
import useAutoReload from '../../hooks/useAutoReload';
import StatusBadge from '../../components/dashboard/StatusBadge';
import ProfileView from '../../components/dashboard/ProfileView';
import ProfileForm from '../../components/dashboard/ProfileForm';
import PasswordChangeForm from '../../components/dashboard/PasswordChangeForm';
import DashModal from '../../components/dashboard/DashModal';
import MessengerPanel from '../../components/dashboard/MessengerPanel';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import Pagination, { usePagination } from '../../components/dashboard/Pagination';
import PaymentModal from '../../components/payment/PaymentModal';
import ReviewModal from '../../components/client/ReviewModal';
import { StarsDisplay } from '../../components/common/Stars';
import '../admin/AdminHome.css';
import '../admin/AdminOrders.css';

const ORDER_BUCKETS = {
  pending: ['draft', 'request_sent', 'pending_review', 'quote_sent', 'quote_accepted', 'payment_pending', 'paid', 'pending'],
  toAssign: ['translator_to_assign', 'translator_unavailable'],
  assigned: ['translator_assigned', 'assigned'],
  inProgress: ['translation_in_progress', 'translation_submitted', 'verification_requested', 'lawyer_notary_in_progress', 'correction_requested', 'new_version', 'verification_validated', 'final_control', 'in_progress'],
  ready: ['ready_to_deliver', 'ready'],
  delivered: ['delivered'],
  done: ['completed'],
  cancelled: ['cancelled', 'refused', 'payment_failed', 'quote_expired'],
};

const orderBucket = (o) => {
  const found = Object.keys(ORDER_BUCKETS).find((b) => ORDER_BUCKETS[b].includes(o.status));
  return found || 'pending';
};

const paymentMethodLabel = (m, c) =>
  ({ card: c.methodCard, edinar: c.methodCard, cash: c.methodCash, transfer: c.methodTransfer, virement: c.methodTransfer, bank_transfer: c.methodTransfer }[m] || m || c.methodOther);

const STEP_FOR_STATUS = {
  draft: 0, request_sent: 0, pending_review: 0, quote_sent: 0, quote_accepted: 0, pending: 0,
  translator_to_assign: 1, translator_unavailable: 1, translator_assigned: 1, assigned: 1,
  payment_pending: 2, paid: 2,
  translation_in_progress: 3, in_progress: 3, translation_submitted: 3,
  verification_requested: 4, lawyer_notary_in_progress: 4, correction_requested: 4,
  new_version: 4, verification_validated: 4, final_control: 4,
  ready_to_deliver: 5, ready: 5, delivered: 6,
  completed: 7,
};

function OrdersTable({ rows, labels }) {
  const { t } = useLanguage();
  if (!rows.length) return <p className="dash-empty">{t.dashboard.empty}</p>;
  return (
    <div className="dash-table-wrap">
      <table className="dash-table">
        <thead>
          <tr>
            <th>{labels.colId}</th>
            <th>{labels.colDocument}</th>
            <th>{labels.colLangs}</th>
            <th>{labels.colStatus}</th>
            <th>{labels.colPrice}</th>
            <th>{labels.colPro}</th>
            <th>{labels.colDate}</th>
            <th>{t.dashboard.actions}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => (
            <tr key={o.id}>
              <td>{o.id}</td>
              <td>{o.document}</td>
              <td>{o.langs}</td>
              <td><StatusBadge status={o.status} labels={t.dashboard.status} /></td>
              <td>{o.price} {o.currency || 'TND'}</td>
              <td>
                {o.translator || <StatusBadge status="toAssign" labels={t.dashboard.status} />}
              </td>
              <td>{o.date}</td>
              <td>
                <Link className="dash-link" to={`/app/orders/${o.id}`}>{t.dashboard.view}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ClientHomePage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [howOpen, setHowOpen] = useState(false);

  useEffect(() => {
    clientApi.overview().then(setData).catch(() => setData(null));
  }, []);

  const stats = data?.stats || { orders: 0, quotes: 0, done: 0, inProgress: 0 };
  const recentOrders = data?.recentOrders || [];

  const kpis = [
    { icon: Package, label: c.statOrders, value: stats.orders, hint: c.kpiAll, cls: 'ah-kpi--teal' },
    { icon: Loader, label: c.statProgress, value: stats.inProgress, hint: c.kpiProgress, cls: 'ah-kpi--navy' },
    { icon: CheckCircle2, label: c.statDone, value: stats.done, hint: c.kpiReady, cls: 'ah-kpi--green' },
  ];

  const shortcuts = [
    { to: '/app/orders', icon: Package, label: c.ctaOrders },
    { to: '/app/documents', icon: FolderOpen, label: c.ctaDocs },
    { to: '/app/payments', icon: CreditCard, label: c.ctaPayments },
    { to: '/app/messages', icon: MessageSquare, label: c.ctaMessages },
  ];

  const reload = () => clientApi.overview().then(setData).catch(() => setData(null));

  return (
    <div className="ah">
      <header className="ah-hero">
        <div className="ah-hero-text">
          <span className="ah-hero-eyebrow"><UserRound size={13} />{c.title}</span>
          <h1>{c.homeTitle}</h1>
          <p>{c.homeSubtitle}</p>
        </div>
        <div className="ah-hero-actions">
          <Link to="/app/new-order" className="ah-hero-btn ah-hero-btn--gold">
            <Plus size={16} />{c.ctaOrder}
          </Link>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={reload}>
            <RefreshCw size={16} />{d.refresh || 'Actualiser'}
          </button>
          <button type="button" className="ah-hero-btn ah-hero-btn--ghost" onClick={() => setHowOpen(true)}>
            <HelpCircle size={16} />{d.howItWorks}
          </button>
        </div>
      </header>

      <div className="ah-kpis">
        {kpis.map((k) => (
          <div className={`ah-kpi ${k.cls}`} key={k.label}>
            <span className="ah-kpi-icon"><k.icon size={22} /></span>
            <div className="ah-kpi-body">
              <em>{k.label}</em>
              <strong>{k.value}</strong>
              <small>{k.hint}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="ah-grid">
        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><ClipboardList size={16} /></span>
              <h2>{c.recentOrders}</h2>
              <Link className="dash-link" to="/app/orders">
                {d.view} <ArrowRight size={13} style={{ verticalAlign: '-2px' }} />
              </Link>
            </div>
            <div className="ah-card-body" style={{ padding: 0 }}>
              <OrdersTable rows={recentOrders} labels={c} />
            </div>
          </section>
        </div>

        <div className="ah-col">
          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Plus size={16} /></span>
              <h2>{d.quickActions || 'Actions rapides'}</h2>
            </div>
            <div className="ah-card-body">
              <div className="ah-shortcuts">
                {shortcuts.map((s) => (
                  <Link className="ah-shortcut" to={s.to} key={s.to}>
                    <s.icon size={17} />{s.label}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="ah-card">
            <div className="ah-card-head">
              <span className="ah-card-head-icon"><Info size={16} /></span>
              <h2>{d.clientInfoTitle}</h2>
            </div>
            <div className="ah-card-body">
              <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                {d.clientInfoOrders}
              </p>
              <p style={{ margin: '10px 0 0', color: 'var(--gray-600)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                <strong>{d.deliverables}</strong> — {d.clientInfoFiles}
              </p>
            </div>
          </section>
        </div>
      </div>

      <DashModal open={howOpen} title={d.howItWorks} onClose={() => setHowOpen(false)}>
        <ol style={{ margin: 0, paddingInlineStart: 18, lineHeight: 1.7, color: 'var(--gray-700)' }}>
          <li>Vous commandez en ligne (connecté).</li>
          <li>La mission est proposée à un professionnel compatible selon <strong>De → Vers</strong>.</li>
          <li>Dès qu’il accepte, vous pouvez régler la commande.</li>
          <li>Le professionnel traduit puis livre le document.</li>
          <li>Vous le téléchargez dans le détail de commande.</li>
        </ol>
      </DashModal>
    </div>
  );
}

export function ClientOrdersPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [orders, setOrders] = useState([]);
  const [payOrder, setPayOrder] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('recent');

  const reload = () => clientApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));

  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useAutoReload(reload);

  const bucketLabels = {
    pending: d.status.pending,
    toAssign: d.status.toAssign,
    assigned: d.status.assigned,
    inProgress: d.status.in_progress,
    ready: d.status.ready,
    delivered: d.status.delivered,
    done: d.status.completed,
    cancelled: d.status.cancelled,
  };

  const bucketCount = (b) => orders.filter((o) => orderBucket(o) === b).length;
  const overdueCount = orders.filter((o) => o.isOverdue).length;

  const filtered = useMemo(() => {
    const rows = orders.filter((o) => {
      if (statusFilter === 'overdue' && !o.isOverdue) return false;
      if (statusFilter !== 'all' && statusFilter !== 'overdue' && orderBucket(o) !== statusFilter) return false;
      if (serviceFilter !== 'all' && (o.category || 'translation') !== serviceFilter) return false;
      if (paymentFilter !== 'all' && (o.paymentStatus || 'unpaid') !== paymentFilter) return false;
      if (dateFrom && (o.date || '') < dateFrom) return false;
      if (dateTo && (o.date || '') > dateTo) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [o.id, o.document, o.langs, o.translator].some((x) => String(x || '').toLowerCase().includes(q));
    });
    return rows.sort((x, y) => {
      switch (sortBy) {
        case 'oldest': return String(x.date || '').localeCompare(String(y.date || ''));
        case 'deadline': {
          const dx = x.deadline || '9999';
          const dy = y.deadline || '9999';
          return dx.localeCompare(dy);
        }
        case 'amount': return (Number(y.price) || 0) - (Number(x.price) || 0);
        default: return String(y.date || '').localeCompare(String(x.date || ''));
      }
    });
  }, [orders, search, statusFilter, serviceFilter, paymentFilter, dateFrom, dateTo, sortBy]);
  const ordersPg = usePagination(filtered, 6);

  const hasFilters = search || statusFilter !== 'all' || serviceFilter !== 'all'
    || paymentFilter !== 'all' || dateFrom || dateTo || sortBy !== 'recent';
  const resetFilters = () => {
    setSearch(''); setStatusFilter('all'); setServiceFilter('all');
    setPaymentFilter('all'); setDateFrom(''); setDateTo(''); setSortBy('recent');
  };

  const heroStats = [
    { id: 'all', icon: Package, label: c.filterAll, value: orders.length },
    { id: 'pending', icon: Inbox, label: d.status.pending, value: bucketCount('pending') },
    { id: 'toAssign', icon: Hourglass, label: d.status.toAssign, value: bucketCount('toAssign') },
    { id: 'inProgress', icon: Loader, label: d.status.in_progress, value: bucketCount('inProgress') },
    { id: 'done', icon: CheckCircle2, label: d.status.completed, value: bucketCount('done') },
    { id: 'overdue', icon: Flame, label: c.overdueLabel, value: overdueCount, tone: 'danger' },
  ];

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><Package size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{c.ordersTitle}</h1>
            <p>{c.ordersSubtitle}</p>
          </div>
          <Link to="/app/new-order" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Plus size={16} />{c.ctaOrder}
          </Link>
        </div>
        <div className="orders-hero-stats" role="tablist">
          {heroStats.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={statusFilter === s.id}
              className={`orders-stat${s.tone ? ` orders-stat--${s.tone}` : ''}${statusFilter === s.id ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(s.id)}
            >
              <span className="orders-stat-icon"><s.icon size={15} /></span>
              <span className="orders-stat-body">
                <strong>{s.value}</strong>
                <em>{s.label}</em>
              </span>
            </button>
          ))}
        </div>
      </header>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />{c.filtersTitle}</span>
          <span className="of-meta">
            <span>{filtered.length} / {orders.length}</span>
            {hasFilters && (
              <button type="button" className="of-reset" onClick={resetFilters}>
                <RotateCcw size={13} />{c.resetFilters}
              </button>
            )}
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>{c.searchLabel}</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={c.searchOrders} />
          </div>
          <div className="of-field">
            <label>{c.colStatus}</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">{c.filterAll}</option>
              <option value="pending">{d.status.pending}</option>
              <option value="toAssign">{d.status.toAssign}</option>
              <option value="assigned">{d.status.assigned}</option>
              <option value="inProgress">{d.status.in_progress}</option>
              <option value="ready">{d.status.ready}</option>
              <option value="delivered">{d.status.delivered}</option>
              <option value="done">{d.status.completed}</option>
              <option value="cancelled">{d.status.cancelled}</option>
              <option value="overdue">{c.overdueLabel}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{c.filterService}</label>
            <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)}>
              <option value="all">{c.filterAll}</option>
              <option value="translation">{c.catTranslation}</option>
              <option value="interpreting">{c.catInterpreting}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{c.payLabel}</label>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="all">{c.filterAll}</option>
              <option value="unpaid">{d.payment.unpaid}</option>
              <option value="pending">{d.payment.pending}</option>
              <option value="paid">{d.payment.paid}</option>
              <option value="refunded">{d.payment.refunded}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{c.fromLabel}</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="of-field">
            <label>{c.toLabel}</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="of-field">
            <label>{c.sortLabel}</label>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">{c.sortRecent}</option>
              <option value="oldest">{c.sortOldest}</option>
              <option value="deadline">{c.sortDeadline}</option>
              <option value="amount">{c.sortAmount}</option>
            </select>
          </div>
        </div>
      </div>

      {ordersPg.pageItems.length === 0 ? (
        <p className="dash-empty">{d.empty}</p>
      ) : (
        <div className="order-cards">
          {ordersPg.pageItems.map((o) => {
            const bucket = orderBucket(o);
            const cardCanPay = Boolean(
              o.translatorConfirmed
              && (o.paymentStatus || 'unpaid') !== 'paid'
              && Number(o.price) > 0
              && bucket !== 'cancelled'
            );
            return (
              <div className="order-card" key={o.id}>
                <div className="order-card-head">
                  <strong>{o.id}</strong>
                  <StatusBadge status={bucket} labels={bucketLabels} />
                </div>
                <div className="order-card-line">
                  <Languages size={15} />{o.langs}
                </div>
                <div className="order-card-line">
                  <FileText size={15} />{o.document} · {o.category === 'interpreting' ? c.catInterpreting : c.catTranslation}
                </div>
                {o.translator ? (
                  <div className="order-card-line">
                    <UserRound size={15} />{c.proLabel} : {o.translator}
                  </div>
                ) : (
                  <div className="order-card-await">
                    <AlertTriangle size={15} />
                    <span><strong>{c.awaitingTitle}</strong>{c.awaitingText}</span>
                  </div>
                )}
                {o.needsReview && (
                  <Link
                    to={`/app/orders/${o.id}`}
                    className="order-card-view"
                    style={{
                      justifyContent: 'center',
                      background: 'var(--gold)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Star size={14} /> {c.reviewBannerCta || 'Évaluer mon expérience'}
                  </Link>
                )}
                <div className="order-card-foot">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <CalendarDays size={14} />{c.orderedOn} {o.date || '—'}
                  </span>
                  <span className="order-card-amount">{o.price} {o.currency || 'TND'}</span>
                </div>
                {cardCanPay && (
                  <button
                    type="button"
                    className="order-card-view"
                    style={{ justifyContent: 'center', border: 0, cursor: 'pointer', font: 'inherit', width: '100%', background: 'var(--gold)' }}
                    onClick={() => setPayOrder(o)}
                  >
                    <CreditCard size={14} /> {c.payNow || 'Payer maintenant'}
                  </button>
                )}
                <Link className="order-card-view" to={`/app/orders/${o.id}`} style={{ justifyContent: 'center' }}>
                  {c.viewOrder}
                </Link>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <Pagination page={ordersPg.page} totalPages={ordersPg.totalPages} total={ordersPg.total} onChange={ordersPg.setPage} />
      </div>
      {payOrder && (
        <PaymentModal
          open
          onClose={() => setPayOrder(null)}
          order={payOrder}
          submit={(payload) => clientApi.createPayment({ orderId: payOrder.id, ...payload })}
          onPaid={() => { setPayOrder(null); reload(); }}
        />
      )}
    </>
  );
}

export function ClientOrderDetailPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [data, setData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [proOpen, setProOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    clientApi.order(id).then(setData).catch(() => setData(null));
    clientApi.payments().then((res) => setPayments(res.payments || [])).catch(() => setPayments([]));
  }, [id]);

  const order = data?.order;

  const onPaid = () => {
    clientApi.order(id).then(setData).catch(() => {});
    clientApi.payments().then((r) => setPayments(r.payments || [])).catch(() => {});
    setMsg(c.paymentSuccess || 'Paiement confirmé');
  };

  const bucket = order ? orderBucket(order) : 'pending';
  const bucketLabels = {
    pending: d.status.pending, toAssign: d.status.toAssign, assigned: d.status.assigned,
    inProgress: d.status.in_progress, ready: d.status.ready, delivered: d.status.delivered,
    done: d.status.completed, cancelled: d.status.cancelled,
  };

  const steps = [c.stepCreated, c.stepAssigned, c.stepPaid, c.stepProgress, c.stepVerify, c.stepDelivery, c.stepDone];
  const curStep = order ? (STEP_FOR_STATUS[order.status] ?? 0) : -1;
  const isCancelled = order ? orderBucket(order) === 'cancelled' : false;

  const sourceDocs = (order?.documents || []).filter((doc) => doc.kind !== 'final');
  const finalDocs = [
    ...(data?.deliverables || []),
    ...(order?.documents || []).filter((doc) => doc.kind === 'final'),
  ];
  const submitReview = async (payload) => {
    setReviewBusy(true);
    setReviewError('');
    try {
      const res = data?.review
        ? await clientApi.updateReview(data.review.id, payload)
        : await clientApi.submitReview(order.id, payload);
      setData((prev) => ({ ...prev, review: res.review, canReview: false }));
      setReviewOpen(false);
      setMsg(c.reviewThanks || 'Merci ! Votre avis sera publié après validation.');
    } catch (e) {
      setReviewError(e.data?.error || 'Envoi impossible');
    } finally {
      setReviewBusy(false);
    }
  };

  const payment = payments.find((p) => p.orderCode === order?.id) || null;
  const proConfirmed = ['accepted', 'in_progress', 'ready', 'delivered'].includes(data?.mission?.status);
  const canPay = Boolean(order && order.paymentStatus !== 'paid' && order.price > 0 && proConfirmed);
  const delivery = order?.deliveryInfo || {};
  const hasDelivery = Boolean(order?.delivery || delivery.address || delivery.trackingNumber || delivery.sentAt || delivery.deliveredAt);

  return (
    <>
      <Link to="/app/orders" className="dash-link" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
        <ChevronLeft size={15} />{c.backOrders || d.backSite}
      </Link>

      {!order ? (
        <p className="dash-empty">{d.empty}</p>
      ) : (
        <div className="ah">
          <header className="ah-hero">
            <div className="ah-hero-text">
              <span className="ah-hero-eyebrow">
                <Package size={13} />
                {order.category === 'interpreting' ? c.catInterpreting : c.catTranslation}
              </span>
              <h1>{order.id}</h1>
              <p>{order.langs} · {order.document}</p>
              <div className="ord-hero-meta">
                <span><CalendarDays size={13} />{order.date || '—'}</span>
                {order.deadline ? <span><Hourglass size={13} />{order.deadline}</span> : null}
                <StatusBadge status={bucket} labels={bucketLabels} />
              </div>
            </div>
            <div className="ah-hero-actions">
              <div className="ord-hero-price">
                <em>{c.colPrice}</em>
                <strong>{order.price} {order.currency || 'TND'}</strong>
                <StatusBadge status={order.paymentStatus || 'unpaid'} labels={d.payment} />
              </div>
              {canPay && (
                <button type="button" className="btn btn-primary" onClick={() => setPayOpen(true)}>
                  <CreditCard size={15} />{c.payNow || 'Payer maintenant'}
                </button>
              )}
            </div>
          </header>

          {data?.canReview && (
            <div
              className="ah-card"
              style={{
                marginBottom: 18,
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                flexWrap: 'wrap',
                border: '1px solid rgba(201, 162, 39, 0.35)',
                background: 'linear-gradient(90deg, #fef9e7, #fdf5d3)',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <span
                  style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--gold)', color: '#fff',
                  }}
                >
                  <Star size={18} />
                </span>
                <span>
                  <strong style={{ display: 'block', fontSize: '0.92rem', color: 'var(--navy)' }}>
                    {c.reviewBannerTitle || 'Votre commande a été livrée'}
                  </strong>
                  <span style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>
                    {order.id} — {order.document} · {order.langs}
                  </span>
                </span>
              </span>
              <button
                type="button"
                className="btn btn-primary"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}
                onClick={() => { setReviewError(''); setReviewOpen(true); }}
              >
                <Star size={15} /> {c.reviewBannerCta || 'Évaluer mon expérience'}
              </button>
            </div>
          )}

          <div className="ah-grid">
            <div className="ah-col">
              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><Loader size={16} /></span>
                  <h2>{c.secProgress}</h2>
                </div>
                <div className="ah-card-body">
                  <ol className="ord-steps">
                    {steps.map((label, i) => (
                      <li key={label} className={i < curStep ? 'is-done' : i === curStep && !isCancelled ? 'is-current' : ''}>
                        <span className="ord-dot">{i < curStep ? <Check size={13} /> : i + 1}</span>
                        {label}
                      </li>
                    ))}
                  </ol>
                </div>
              </section>

              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><FileText size={16} /></span>
                  <h2>{c.secDocs}</h2>
                </div>
                <div className="ah-card-body">
                  <h4 style={{ margin: '0 0 8px', fontSize: '0.82rem', color: 'var(--gray-500)' }}>{c.secDocsSent}</h4>
                  {sourceDocs.length ? (
                    <ul className="dash-file-list">
                      {sourceDocs.map((f) => (
                        <li key={f.id}>
                          <span>{f.name}</span>
                          {f.url && (
                            <span style={{ display: 'inline-flex', gap: 10 }}>
                              <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="dash-link"><Eye size={13} style={{ verticalAlign: '-2px' }} /> {d.view}</a>
                              <a href={fileUrl(f.url)} download className="dash-link"><Download size={13} style={{ verticalAlign: '-2px' }} /> {c.download}</a>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="ah-empty">{d.empty}</p>}
                  <h4 style={{ margin: '14px 0 8px', fontSize: '0.82rem', color: 'var(--gray-500)' }}>{c.secDocsFinal}</h4>
                  {finalDocs.length ? (
                    <ul className="dash-file-list">
                      {finalDocs.map((f) => (
                        <li key={f.id}>
                          <span>{f.name}</span>
                          {f.url && (
                            <span style={{ display: 'inline-flex', gap: 10 }}>
                              <a href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="dash-link"><Eye size={13} style={{ verticalAlign: '-2px' }} /> {d.view}</a>
                              <a href={fileUrl(f.url)} download className="dash-link"><Download size={13} style={{ verticalAlign: '-2px' }} /> {c.download}</a>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : <p className="ah-empty">{d.empty}</p>}
                </div>
              </section>

              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><History size={16} /></span>
                  <h2>{c.secHistory}</h2>
                </div>
                <div className="ah-card-body">
                  {(order.history || []).length ? (
                    <ul className="ord-hist">
                      {[...order.history].reverse().map((h, i) => (
                        <li key={i}>
                          <time>{h.at ? new Date(h.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}</time>
                          <div>
                            <strong>{h.action}</strong>
                            {h.detail ? <p>{h.detail}</p> : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="ah-empty">{d.empty}</p>}
                </div>
              </section>
            </div>

            <div className="ah-col">
              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><UserRound size={16} /></span>
                  <h2>{c.secPro}</h2>
                </div>
                <div className="ah-card-body">
                  {order.translator ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {data?.translatorProfile?.photoUrl ? (
                          <img
                            src={fileUrl(data.translatorProfile.photoUrl)}
                            alt={order.translator}
                            style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0', flexShrink: 0 }}
                          />
                        ) : (
                          <span style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(26,122,122,0.12)', color: 'var(--teal)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <UserRound size={24} />
                          </span>
                        )}
                        <p style={{ margin: 0 }}>
                          <strong style={{ fontSize: '1rem' }}>{order.translator}</strong>
                          {(data?.translatorProfile?.accredited || data?.translatorProfile?.sworn) && (
                            <span style={{ marginInlineStart: 6, fontSize: '0.72rem', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: 999, padding: '1px 8px', verticalAlign: 2 }}>
                              ✔ {c.proVerified || 'Vérifié'}
                            </span>
                          )}
                          <br />
                          <span style={{ color: 'var(--gray-500)', fontSize: '0.82rem' }}>
                            {data?.translatorProfile?.title || order.langs}
                          </span>
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                        {data?.translatorProfile && (
                          <button type="button" className="ord-contact-btn" onClick={() => setProOpen(true)}>
                            <Eye size={15} />{c.viewProProfile || 'Voir le profil'}
                          </button>
                        )}
                        <Link
                          to="/app/messages"
                          className="ord-contact-btn"
                          style={data?.translatorProfile ? { background: 'transparent', color: 'var(--teal)', border: '1px solid var(--teal)', boxShadow: 'none' } : undefined}
                        >
                          <MessageSquare size={15} />{c.contactPro}
                        </Link>
                      </div>
                      {data?.review && (
                        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--gray-50)', border: '1px solid var(--gray-100)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                            <StarsDisplay value={data.review.rating} size={14} showValue />
                            {data.review.editable && (
                              <button
                                type="button"
                                className="dash-link"
                                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: '0.78rem' }}
                                onClick={() => { setReviewError(''); setReviewOpen(true); }}
                              >
                                <Pencil size={11} style={{ verticalAlign: '-1px' }} /> {c.editReview || 'Modifier'}
                              </button>
                            )}
                          </div>
                          {data.review.comment && (
                            <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: 'var(--gray-600)' }}>
                              « {data.review.comment} »
                            </p>
                          )}
                          <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)' }}>
                            {c.yourReview || 'Votre avis'} · {data.review.createdAt ? new Date(data.review.createdAt).toLocaleDateString('fr-FR') : ''}
                            {data.review.status === 'pending' ? ` · ${c.reviewPending || 'en attente de modération'}` : ''}
                            {data.review.status === 'hidden' ? ` · ${c.reviewHidden || 'masqué par la modération'}` : ''}
                          </span>
                        </div>
                      )}
                    </>
                  ) : !['cancelled', 'done', 'delivered'].includes(bucket) ? (
                    <div className="order-card-await">
                      <AlertTriangle size={15} />
                      <span><strong>{c.awaitingTitle}</strong>{c.awaitingText}</span>
                    </div>
                  ) : (
                    <p className="ah-empty">{d.empty}</p>
                  )}
                </div>
              </section>

              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><Banknote size={16} /></span>
                  <h2>{c.secPayment}</h2>
                  <StatusBadge status={order.paymentStatus || 'unpaid'} labels={d.payment} />
                </div>
                <div className="ah-card-body">
                  <dl className="ord-kv">
                    <dt>{c.payAmount}</dt><dd><strong>{order.price} {order.currency || 'TND'}</strong></dd>
                    {payment && (
                      <>
                        <dt>{c.payMethod}</dt><dd>{paymentMethodLabel(payment.method, c)}</dd>
                        <dt>{c.payDate}</dt><dd>{payment.paidAt || payment.createdAt || '—'}</dd>
                        <dt>{c.payRef}</dt><dd>{payment.reference || payment.id || '—'}</dd>
                      </>
                    )}
                  </dl>
                  {order.paymentStatus === 'paid' ? (
                    <div className="order-card-await" style={{ marginTop: 12, borderColor: 'var(--teal)', color: 'var(--teal)' }}>
                      <CheckCircle2 size={15} />
                      <span>{c.payDone || 'Paiement confirmé — votre commande est en cours de traitement.'}</span>
                    </div>
                  ) : !proConfirmed ? (
                    <div className="order-card-await" style={{ marginTop: 12 }}>
                      <AlertTriangle size={15} />
                      <span>{c.payAwaitingPro || 'Paiement disponible dès qu’un professionnel aura accepté votre commande.'}</span>
                    </div>
                  ) : canPay ? (
                    <div className="order-card-await" style={{ marginTop: 12 }}>
                      <AlertTriangle size={15} />
                      <span>{c.payReady || 'Un professionnel a accepté votre commande — réglez le paiement pour lancer le travail.'}</span>
                    </div>
                  ) : null}
                  {msg && (
                    <div className="dash-toast dash-toast--float" role="status">
                      <span>{msg}</span>
                      <button type="button" onClick={() => setMsg('')} aria-label="Fermer">
                        <X size={13} aria-hidden="true" />
                      </button>
                    </div>
                  )}
                </div>
              </section>

              {hasDelivery && (
                <section className="ah-card">
                  <div className="ah-card-head">
                    <span className="ah-card-head-icon"><Truck size={16} /></span>
                    <h2>{c.secDelivery}</h2>
                  </div>
                  <div className="ah-card-body">
                    <dl className="ord-kv">
                      <dt>{c.deliveryMode}</dt><dd>{order.delivery || '—'}</dd>
                      {delivery.address ? (<><dt>{c.deliveryAddress}</dt><dd>{delivery.address}</dd></>) : null}
                      <dt>{c.deliveryPlanned}</dt><dd>{order.deadline || '—'}</dd>
                      {delivery.trackingNumber ? (<><dt>{c.deliveryTracking}</dt><dd>{delivery.trackingNumber}</dd></>) : null}
                    </dl>
                  </div>
                </section>
              )}

              <section className="ah-card">
                <div className="ah-card-head">
                  <span className="ah-card-head-icon"><MessageSquare size={16} /></span>
                  <h2>{c.secMessages}</h2>
                </div>
                <div className="ah-card-body">
                  <p style={{ margin: 0, color: 'var(--gray-600)', fontSize: '0.85rem' }}>{c.messagesHint}</p>
                  <Link to="/app/messages" className="dash-btn-sm" style={{ marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <MessageSquare size={14} />{c.goMessages}
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}
      <DashModal
        open={proOpen && Boolean(data?.translatorProfile)}
        title={c.proProfileTitle || 'Profil du professionnel'}
        onClose={() => setProOpen(false)}
      >
        {data?.translatorProfile && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {data.translatorProfile.photoUrl ? (
                <img
                  src={fileUrl(data.translatorProfile.photoUrl)}
                  alt={data.translatorProfile.name}
                  style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0', flexShrink: 0 }}
                />
              ) : (
                <span style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(26,122,122,0.12)', color: 'var(--teal)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <UserRound size={30} />
                </span>
              )}
              <div>
                <strong style={{ fontSize: '1.05rem' }}>{data.translatorProfile.name}</strong>
                {(data.translatorProfile.accredited || data.translatorProfile.sworn) && (
                  <span style={{ marginInlineStart: 6, fontSize: '0.72rem', background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', borderRadius: 999, padding: '1px 8px', verticalAlign: 2 }}>
                    ✔ {c.proVerified || 'Vérifié'}
                  </span>
                )}
                <br />
                <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>
                  {data.translatorProfile.title || order?.langs}
                </span>
                <br />
                <span style={{ color: 'var(--teal)', fontSize: '0.78rem', fontWeight: 600 }}>
                  {c.proAccepted || 'Commande acceptée'}
                </span>
              </div>
            </div>

            {data.translatorProfile.bio && (
              <p style={{ margin: '12px 0 0', fontSize: '0.85rem', color: 'var(--gray-600)', fontStyle: 'italic' }}>
                {data.translatorProfile.bio}
              </p>
            )}

            <dl className="ord-kv" style={{ marginTop: 12 }}>
              {data.translatorProfile.languages?.length > 0 && (
                <>
                  <dt>{c.proLanguages || 'Langues'}</dt>
                  <dd>{data.translatorProfile.languages.join(' · ')}</dd>
                </>
              )}
              {data.translatorProfile.specialties?.length > 0 && (
                <>
                  <dt>{c.proSpecialties || 'Spécialités'}</dt>
                  <dd>{data.translatorProfile.specialties.join(', ')}</dd>
                </>
              )}
              {data.translatorProfile.experienceYears != null && (
                <>
                  <dt>{c.proExperience || 'Expérience'}</dt>
                  <dd>{data.translatorProfile.experienceYears} {c.proYears || 'an(s)'}</dd>
                </>
              )}
              {data.translatorProfile.rating != null && (
                <>
                  <dt>{c.proRating || 'Note'}</dt>
                  <dd>{data.translatorProfile.rating}/5</dd>
                </>
              )}
              {data.translatorProfile.completedMissions != null && (
                <>
                  <dt>{c.proMissions || 'Missions réalisées'}</dt>
                  <dd>{data.translatorProfile.completedMissions}</dd>
                </>
              )}
              {data.translatorProfile.sworn && (
                <>
                  <dt>{c.proSworn || 'Assermenté'}</dt>
                  <dd>✔</dd>
                </>
              )}
              {data.translatorProfile.expertiseAreas?.length > 0 && (
                <>
                  <dt>{c.proExpertise || 'Domaines'}</dt>
                  <dd>{data.translatorProfile.expertiseAreas.join(', ')}</dd>
                </>
              )}
              {(data.translatorProfile.qualifications?.length > 0 || data.translatorProfile.accreditationBody) && (
                <>
                  <dt>{c.proQualifications || 'Qualifications'}</dt>
                  <dd>
                    {[
                      ...(data.translatorProfile.qualifications || []),
                      data.translatorProfile.accreditationBody,
                    ].filter(Boolean).join(' · ')}
                  </dd>
                </>
              )}
            </dl>

            <p style={{ margin: '12px 0 0', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
              {c.proPrivacyNote || 'Les coordonnées personnelles du professionnel ne sont pas affichées — les échanges passent par la messagerie.'}
            </p>

            {canPay && (
              <div style={{ marginTop: 14 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { setProOpen(false); setPayOpen(true); }}
                >
                  <CreditCard size={15} />{c.payNow || 'Procéder au paiement'}
                </button>
              </div>
            )}
          </div>
        )}
      </DashModal>
      {order && (
        <ReviewModal
          open={reviewOpen}
          order={order}
          existing={data?.review}
          busy={reviewBusy}
          error={reviewError}
          onClose={() => setReviewOpen(false)}
          onSubmit={submitReview}
        />
      )}
      {order && (
        <PaymentModal
          open={payOpen}
          onClose={() => setPayOpen(false)}
          order={order}
          submit={(payload) => clientApi.createPayment({ orderId: id, ...payload })}
          onPaid={onPaid}
        />
      )}
    </>
  );
}

export function ClientMessagesPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [messages, setMessages] = useState([]);
  const [orders, setOrders] = useState([]);
  const [body, setBody] = useState('');
  const [toSel, setToSel] = useState('');
  const [threadKey, setThreadKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const pros = useMemo(
    () => (orders || []).filter((o) => o.translatorId).map((o) => ({ tid: o.translatorId, oid: o._id, name: o.translator || 'Pro', sub: o.id })),
    [orders]
  );

  const msgContacts = useMemo(
    () => [
      { key: '', name: c.administration, sub: 'Tarjemlek' },
      ...pros.map((p) => ({ key: `${p.tid}|${p.oid}`, name: p.name, sub: p.sub })),
    ],
    [pros, c.administration]
  );

  const threads = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (!map.has(m.threadKey)) map.set(m.threadKey, { key: m.threadKey, people: new Map(), last: m.createdAt, preview: '' });
      const thread = map.get(m.threadKey);
      [m.from, m.to].forEach((p) => {
        if (p && p.role !== 'client') thread.people.set(p.id, p);
      });
      if (new Date(m.createdAt) >= new Date(thread.last || 0)) {
        thread.last = m.createdAt;
        thread.preview = m.body;
      }
    });
    return Array.from(map.values())
      .sort((x, y) => new Date(y.last || 0) - new Date(x.last || 0))
      .map((thread) => ({
        ...thread,
        name: Array.from(thread.people.values()).map((p) => (p.role === 'admin' ? c.administration : p.name)).filter(Boolean).join(' ↔ ') || thread.key,
      }));
  }, [messages, c.administration]);

  const reload = () => clientApi.messages().then((res) => setMessages(res.messages || [])).catch(() => setMessages([]));

  useEffect(() => {
    reload();
    clientApi.orders().then((res) => setOrders(res.orders || [])).catch(() => setOrders([]));
    const timer = setInterval(reload, 4000);
    return () => clearInterval(timer);
  }, []);

  const pickThread = (key) => {
    setThreadKey(key);
    if (key?.startsWith('order:')) {
      const pro = pros.find((p) => `order:${p.oid}` === key);
      setToSel(pro ? `${pro.tid}|${pro.oid}` : '');
    } else {
      setToSel('');
    }
  };

  const filtered = threadKey && threadKey !== 'new' ? messages.filter((m) => m.threadKey === threadKey) : [];

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) {
      setMsg(t.auth?.invalidPayload || 'Veuillez remplir tous les champs obligatoires.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      const [tid, oid] = toSel ? toSel.split('|') : [null, null];
      await clientApi.sendMessage({ body, ...(tid ? { toId: tid, orderId: oid, threadKey: `order:${oid}` } : {}) });
      const key = tid ? `order:${oid}` : threadKey && threadKey !== 'new' ? threadKey : `client:${user?.id}`;
      setBody('');
      reload();
      setThreadKey(key);
    } catch (err) {
      setMsg(err.data?.error || d.empty);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.messagesTitle}</h1>
        <p>{c.messagesSubtitle}</p>
      </header>
      {msg && <p className="dash-toast">{msg}</p>}
      <MessengerPanel
        threads={threads}
        activeKey={threadKey}
        onSelect={pickThread}
        messages={filtered}
        myRole="client"
        body={body}
        setBody={setBody}
        onSend={send}
        busy={busy}
        labels={{
          conversations: 'Conversations',
          newMessage: 'Nouveau message',
          selectConversation: 'Sélectionnez une conversation',
          writeMessage: 'Écrire un message…',
          empty: d.empty,
          send: c.sendMessage,
        }}
        contacts={msgContacts}
        contactKey={toSel}
        onPickContact={setToSel}
        contactsTitle={c.recipient}
      />
    </>
  );
}

export function ClientProfilePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const c = t.dashboard.client;

  if (editing) {
    return <ProfileForm space="client" onBack={() => setEditing(false)} />;
  }

  return (
    <>
      <header
        className="dash-page-head"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <h1>{c.profileTitle}</h1>
          <p>{c.profileSubtitle}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn"
            onClick={() => setEditing(true)}
            style={{ background: '#fff', color: 'var(--navy)', border: '1px solid var(--gray-200)' }}
          >
            <Pencil size={15} /> {t.dashboard?.edit || 'Modifier le profil'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setPasswordOpen(true)}>
            <Lock size={15} /> {t.auth?.changePassword || 'Changer le mot de passe'}
          </button>
        </div>
      </header>

      <ProfileView user={user} space="client" />

      <DashModal
        open={passwordOpen}
        title={t.auth?.changePassword || 'Changer le mot de passe'}
        onClose={() => setPasswordOpen(false)}
      >
        <PasswordChangeForm />
      </DashModal>
    </>
  );
}

export function ClientNotificationsPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  const reload = () => clientApi.notifications().then((res) => setItems(res.notifications || [])).catch(() => setItems([]));

  useEffect(() => {
    reload();
    const timer = setInterval(reload, 10000);
    return () => clearInterval(timer);
  }, []);

  const openNotif = (n) => {
    if (!n.readAt) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)));
      clientApi.readNotifications({ id: n.id }).catch(() => {});
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await clientApi.readNotifications();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
    } catch { /* ignore */ }
  };

  return (
    <>
      <header className="dash-page-head">
        <h1>{c.notificationsTitle || 'Notifications'}</h1>
        <p>{c.notificationsSubtitle || 'Vos dernières alertes.'}</p>
      </header>
      <NotificationsPanel
        items={items}
        onOpen={openNotif}
        onMarkAll={markAll}
        labels={{ all: 'Toutes', unread: 'Non lues', markAllRead: c.markAllRead || 'Tout marquer comme lu', empty: d.empty }}
      />
    </>
  );
}

export function ClientDocumentsPage() {
  const { t } = useLanguage();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [docs, setDocs] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [orderFilter, setOrderFilter] = useState('all');

  useEffect(() => {
    clientApi.documents().then((res) => setDocs(res.documents || [])).catch(() => setDocs([]));
  }, []);

  const typeOf = (doc) => {
    if (doc.kind === 'source') return 'sent';
    if (doc.kind === 'draft') return 'progress';
    if (doc.kind === 'final') {
      return ['ready', 'ready_to_deliver', 'delivered', 'completed'].includes(doc.orderStatus) ? 'final' : 'translated';
    }
    return 'sent';
  };
  const typeLabels = {
    sent: c.docSent, progress: c.docInProgress, translated: c.docTranslated, final: c.docFinal,
  };
  const typeBadge = { sent: 'pending', progress: 'inProgress', translated: 'assigned', final: 'done' };
  const docStatus = { sent: d.status.sent, progress: d.status.in_progress, translated: d.status.validated, final: c.docAvailable };
  const docStatusBadge = { sent: 'sent', progress: 'inProgress', translated: 'ready', final: 'done' };

  const orderCodes = [...new Set(docs.map((doc) => doc.orderCode).filter(Boolean))];
  const filtered = docs.filter((doc) => {
    const type = typeOf(doc);
    if (typeFilter !== 'all' && type !== typeFilter) return false;
    if (orderFilter !== 'all' && doc.orderCode !== orderFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return [doc.name, doc.orderCode, typeLabels[type]].some((x) => String(x || '').toLowerCase().includes(q));
  });
  const docsPg = usePagination(filtered, 8);

  const hasFilters = search || typeFilter !== 'all' || orderFilter !== 'all';
  const resetFilters = () => { setSearch(''); setTypeFilter('all'); setOrderFilter('all'); };

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><FolderOpen size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{c.documentsTitle}</h1>
            <p>{c.documentsSubtitle}</p>
          </div>
        </div>
      </header>

      <div className="orders-filters">
        <div className="orders-filters-head">
          <span className="of-title"><SlidersHorizontal size={13} />{c.filtersTitle}</span>
          <span className="of-meta">
            <span>{filtered.length} / {docs.length}</span>
            {hasFilters && (
              <button type="button" className="of-reset" onClick={resetFilters}>
                <RotateCcw size={13} />{c.resetFilters}
              </button>
            )}
          </span>
        </div>
        <div className="orders-filters-grid">
          <div className="of-field">
            <label>{c.searchLabel}</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={c.searchDocs} />
          </div>
          <div className="of-field">
            <label>{c.docType}</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">{c.filterAll}</option>
              <option value="sent">{c.docSent}</option>
              <option value="translated">{c.docTranslated}</option>
              <option value="final">{c.docFinal}</option>
            </select>
          </div>
          <div className="of-field">
            <label>{c.colOrder}</label>
            <select value={orderFilter} onChange={(e) => setOrderFilter(e.target.value)}>
              <option value="all">{c.filterAll}</option>
              {orderCodes.map((code) => <option key={code} value={code}>{code}</option>)}
            </select>
          </div>
        </div>
      </div>

      <section className="ah-card">
        <div className="ah-card-body" style={{ padding: 0 }}>
          {docsPg.pageItems.length === 0 ? (
            <p className="ah-empty">{c.noDocuments || d.empty}</p>
          ) : (
            <div className="dash-table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>{c.colDoc}</th>
                    <th>{c.colOrder}</th>
                    <th>{c.docType}</th>
                    <th>{c.colDate}</th>
                    <th>{c.colStatus}</th>
                    <th>{d.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {docsPg.pageItems.map((doc) => {
                    const type = typeOf(doc);
                    return (
                      <tr key={doc.id}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <FileText size={15} style={{ color: 'var(--teal)' }} />{doc.name}
                          </span>
                        </td>
                        <td>
                          {doc.orderCode ? (
                            <Link className="dash-link" to={`/app/orders/${doc.orderCode}`}>{doc.orderCode}</Link>
                          ) : '—'}
                        </td>
                        <td><StatusBadge status={typeBadge[type]} labels={{ [typeBadge[type]]: typeLabels[type] }} /></td>
                        <td>{doc.uploadedAt ? new Date(doc.uploadedAt).toISOString().slice(0, 10) : '—'}</td>
                        <td><StatusBadge status={docStatusBadge[type]} labels={{ [docStatusBadge[type]]: docStatus[type] }} /></td>
                        <td>
                          {doc.url ? (
                            <span style={{ display: 'inline-flex', gap: 10 }}>
                              <a href={fileUrl(doc.url)} target="_blank" rel="noreferrer" className="dash-link">
                                <Eye size={13} style={{ verticalAlign: '-2px' }} /> {d.view}
                              </a>
                              <a href={fileUrl(doc.url)} download className="dash-link">
                                <Download size={13} style={{ verticalAlign: '-2px' }} /> {c.download}
                              </a>
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <div style={{ marginTop: 14 }}>
        <Pagination page={docsPg.page} totalPages={docsPg.totalPages} total={docsPg.total} onChange={docsPg.setPage} />
      </div>
    </>
  );
}

export function ClientPaymentsPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const c = t.dashboard.client;
  const d = t.dashboard;
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null);
  const paymentsPg = usePagination(payments, 8);

  useEffect(() => {
    clientApi.payments().then((res) => setPayments(res.payments || [])).catch(() => setPayments([]));
  }, []);

  const money = (n, cur = 'TND') => `${n} ${cur}`;
  const sum = (list) => list.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  const paid = payments.filter((p) => p.status === 'paid');
  const pending = payments.filter((p) => p.status === 'pending');
  const refunded = payments.filter((p) => p.status === 'refunded');
  const methodLabel = (m) => paymentMethodLabel(m, c);

  const kpis = [
    { icon: CreditCard, label: c.statTotalPaid, value: money(sum(paid), paid[0]?.currency), tone: 'green' },
    { icon: Hourglass, label: c.statPendingPayments, value: `${pending.length} · ${money(sum(pending), pending[0]?.currency)}`, tone: 'amber' },
    { icon: RotateCcw, label: c.statRefunds, value: `${refunded.length} · ${money(sum(refunded), refunded[0]?.currency)}`, tone: 'violet' },
  ];

  return (
    <>
      <header className="dash-page-head orders-hero">
        <div className="orders-hero-top">
          <span className="orders-hero-icon"><CreditCard size={22} /></span>
          <div className="orders-hero-text" style={{ flex: 1 }}>
            <h1>{c.paymentsTitle || 'Mes paiements'}</h1>
            <p>{c.paymentsSubtitle || 'Historique et suivi de vos paiements.'}</p>
          </div>
        </div>
      </header>

      <div className="ah-kpis">
        {kpis.map((k) => (
          <div key={k.label} className={`ah-kpi ah-kpi--${k.tone}`}>
            <span className="ah-kpi-icon"><k.icon size={20} /></span>
            <div className="ah-kpi-body">
              <strong>{k.value}</strong>
              <span>{k.label}</span>
            </div>
          </div>
        ))}
      </div>

      <section className="ah-card" style={{ marginTop: 14 }}>
        <div className="ah-card-body" style={{ padding: 0 }}>
          {paymentsPg.pageItems.length === 0 ? (
            <p className="ah-empty">{d.empty}</p>
          ) : (
            <div className="dash-table-wrap" style={{ border: 0, borderRadius: 0 }}>
              <table className="dash-table">
                <thead>
                  <tr>
                    <th>{c.colPayment}</th>
                    <th>{c.colOrder}</th>
                    <th>{c.payAmount}</th>
                    <th>{c.colMethod}</th>
                    <th>{c.colStatus}</th>
                    <th>{c.colDate}</th>
                    <th>{c.invoice}</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentsPg.pageItems.map((p) => (
                    <tr key={p.id}>
                      <td><strong>{p.id}</strong></td>
                      <td>
                        {p.orderCode ? (
                          <Link className="dash-link" to={`/app/orders/${p.orderCode}`}>{p.orderCode}</Link>
                        ) : '—'}
                      </td>
                      <td>{money(p.amount, p.currency)}</td>
                      <td>{methodLabel(p.method)}</td>
                      <td><StatusBadge status={p.status} labels={d.payment} /></td>
                      <td>{p.paidAt || p.createdAt || '—'}</td>
                      <td>
                        {p.status === 'paid' ? (
                          <button type="button" className="dash-btn-sm" onClick={() => setSelected(p)}>
                            <Receipt size={13} style={{ verticalAlign: '-2px' }} /> {c.invoice}
                          </button>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
      <div style={{ marginTop: 14 }}>
        <Pagination page={paymentsPg.page} totalPages={paymentsPg.totalPages} total={paymentsPg.total} onChange={paymentsPg.setPage} />
      </div>

      <DashModal
        open={Boolean(selected)}
        title={c.invoice}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <>
            <div className="invoice-doc">
              <div className="invoice-doc-head">
                <div>
                  <strong className="invoice-doc-brand">Tarjemlek</strong>
                  <span>Plateforme de traduction certifiée</span>
                </div>
                <div className="invoice-doc-num">
                  <strong>{c.invoiceNo} {selected.invoice?.code || `F-${selected.id}`}</strong>
                  <span>{selected.invoice?.issuedAt || selected.paidAt || selected.createdAt || ''}</span>
                </div>
              </div>
              <div className="invoice-doc-parties">
                <div>
                  <span>{c.invoiceBilledTo}</span>
                  <strong>{selected.client || user?.name || '—'}</strong>
                  <span>{selected.clientEmail || user?.email || ''}</span>
                </div>
                <div>
                  <span>{c.colOrder}</span>
                  <strong>{selected.orderCode || '—'}</strong>
                  <span>{c.payRef}: {selected.reference || '—'}</span>
                </div>
              </div>
              <table className="invoice-doc-table">
                <thead>
                  <tr><th>{c.colDocument}</th><th>{c.payAmount}</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{c.invoiceDesc} {selected.orderCode || ''}</td>
                    <td>{money(selected.amount, selected.currency)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td>{c.invoiceTotal}</td>
                    <td>{money(selected.amount, selected.currency)}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="invoice-doc-meta">
                <span>{c.colMethod}: {methodLabel(selected.method)}</span>
                <StatusBadge status={selected.status} labels={d.payment} />
                <span>{c.colDate}: {selected.paidAt || selected.createdAt || '—'}</span>
              </div>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: 14 }}
              onClick={() => window.print()}
            >
              <Printer size={15} style={{ verticalAlign: '-2px' }} /> {c.printInvoice}
            </button>
          </>
        )}
      </DashModal>
    </>
  );
}

